import type {
  AluminiumAISampleComposition,
  AluminiumComposition,
  DetectedHdpeContent,
  DetectedPetContent,
  DetectedUbcContent,
  EvaluatedSpecification,
  EvidenceUploadMode,
  HDPEComposition,
  PETComposition,
  SimpleEvaluatedSpecification,
  SimpleMaterialQualityCategory,
} from 'api';
import { AIModelChoices } from 'api';
import { logger } from 'monitor';
import {
  AluminiumCompositionDataCard,
  HDPECompositionDataCard,
  MaterialNotAnalysedDataCard,
  PETCompositionDataCard,
  TradeCompanySessionLooseDataCard,
} from '../../../data-cards';
import { isAluAiModel, isHdpeAiModel, isLooseAiModel, isOtherAiModel, isPetBaledAiModel } from '../../../evidence';
import { isAluminiumAISampleComposition } from '../../utils/isAluminiumAISampleComposition/isAluminiumAISampleComposition';
import { EvaluatedSpecsBreakdown } from '../EvaluatedSpecsBreakdown/EvaluatedSpecsBreakdown';

type EvaluatedSpecificationLike = EvaluatedSpecification | SimpleEvaluatedSpecification;
type MaterialCategoryContext = Pick<SimpleMaterialQualityCategory, 'id' | 'name'> | null;

type Props = {
  isLoading: boolean;
  parentId?: number;
  uploadMode?: EvidenceUploadMode;
  materialCategory?: MaterialCategoryContext;
  detectedPetContent?: DetectedPetContent;
  detectedUbcContent?: DetectedUbcContent;
  detectedHdpeContent?: DetectedHdpeContent;
  aluComposition?: AluminiumComposition | AluminiumAISampleComposition;
  hdpeComposition?: HDPEComposition;
  petComposition?: PETComposition;
  evaluatedSpecs?: EvaluatedSpecificationLike[];
  totalWeight?: number;
  model: AIModelChoices;
  showAlerts?: boolean;
  latestRunAIVersion?: string;
};

export const QualityResults = ({
  model,
  isLoading,
  parentId,
  uploadMode,
  materialCategory,
  detectedPetContent,
  detectedUbcContent,
  detectedHdpeContent,
  aluComposition,
  hdpeComposition,
  petComposition,
  evaluatedSpecs,
  totalWeight,
  showAlerts = true,
  latestRunAIVersion,
}: Props) => {
  if (isOtherAiModel(model)) {
    return <MaterialNotAnalysedDataCard isLoading={isLoading} />;
  }

  if (evaluatedSpecs && evaluatedSpecs.length > 0) {
    const composition =
      detectedUbcContent?.aluminium_composition ??
      aluComposition ??
      detectedHdpeContent?.hdpe_composition ??
      hdpeComposition ??
      petComposition ??
      detectedPetContent?.pet_composition;

    if (!composition) {
      logger.error('Evaluated specs are present but composition is missing');
      return <MaterialNotAnalysedDataCard isLoading={isLoading} />;
    }

    return (
      <EvaluatedSpecsBreakdown
        specs={evaluatedSpecs}
        composition={composition}
        detectedContent={detectedPetContent ?? detectedUbcContent ?? detectedHdpeContent ?? undefined}
        parentId={parentId}
        uploadMode={uploadMode}
        showAlerts={showAlerts}
        latestRunAIVersion={latestRunAIVersion}
        materialCategory={materialCategory}
      />
    );
  }

  if (isLooseAiModel(model)) {
    return (
      <TradeCompanySessionLooseDataCard
        isLoading={isLoading}
        totalWeight={totalWeight}
        composition={aluComposition && isAluminiumAISampleComposition(aluComposition) ? aluComposition : petComposition}
        aiModel={model}
        latestRunAIVersion={latestRunAIVersion}
      />
    );
  }
  if (isAluAiModel(model)) {
    return (
      <AluminiumCompositionDataCard
        aluminiumComposition={
          detectedUbcContent?.aluminium_composition ??
          (aluComposition && !isAluminiumAISampleComposition(aluComposition) ? aluComposition : undefined)
        }
        detectedUbcContent={detectedUbcContent ?? undefined}
        isLoading={isLoading}
        aiModel={model}
        parentId={parentId}
        uploadMode={uploadMode}
        latestRunAIVersion={latestRunAIVersion}
        materialCategory={materialCategory}
      />
    );
  }

  if (isHdpeAiModel(model)) {
    return (
      <HDPECompositionDataCard
        hdpeComposition={detectedHdpeContent?.hdpe_composition ?? hdpeComposition}
        detectedHdpeContent={detectedHdpeContent ?? undefined}
        isLoading={isLoading}
        parentId={parentId}
        uploadMode={uploadMode}
        latestRunAIVersion={latestRunAIVersion}
        materialCategory={materialCategory}
      />
    );
  }

  if (isPetBaledAiModel(model)) {
    return (
      <PETCompositionDataCard
        petComposition={detectedPetContent?.pet_composition ?? petComposition}
        detectedPetContent={detectedPetContent ?? undefined}
        isLoading={isLoading}
        parentId={parentId}
        uploadMode={uploadMode}
        modelName={model}
        latestRunAIVersion={latestRunAIVersion}
        materialCategory={materialCategory}
      />
    );
  }
  logger.error(`Unknown AI model ${model}`);
  return <MaterialNotAnalysedDataCard />;
};
